
import { useEffect, useState } from "react";
import { DEFAULT_BELTS } from "./types";
import { usePermissions } from "@/contexts/unified-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { indexedDBService } from "@/services/offline/indexeddb";
import { useOfflineState } from "@/contexts/offline";
import { supabase } from "@/integrations/supabase/client";

const BASE_STRIPE_SPACING = 45;
const MINIMUM_END_BUFFER = 12;

export function useStripeSpacings() {
  const [stripeSpacings, setStripeSpacings] = useState<number[]>([]);
  const { user } = usePermissions();
  const isMobile = useIsMobile();
  const { state } = useOfflineState();
  
  // Use consistent 4px width for tick marks regardless of device
  const TICK_WIDTH = 4;
  // Adjust spacing per tick based on device type
  const spacingPerTick = isMobile ? 20 : 10;

  useEffect(() => {
    async function fetchTrainingSessions() {
      if (!user?.id) return;
      try {
        // Initialize IndexedDB if needed
        await indexedDBService.initialize();

        // Use offline-first approach: try cache first, then network if online
        let sessions;

        if (!state.isOnline) {
          // If offline, use cached data only
          sessions = await indexedDBService.trainingSessions.getByUserId(user.id);
        } else {
          // If online, try cache first for better performance
          const cachedSessions = await indexedDBService.trainingSessions.getByUserId(user.id);

          if (cachedSessions.length > 0) {
            sessions = cachedSessions;
          } else {
            // Fallback to direct Supabase call if no cache (shouldn't happen with proper initial loading)
            const { data, error } = await supabase
              .from('training_sessions')
              .select('belt_rank, stripe_count')
              .eq('user_id', user.id);

            if (error) throw error;
            sessions = data || [];
          }
        }

        const tickCountMap: Record<string, number> = {};
        for (const session of sessions) {
          const belt = (session.belt_rank || 'white').toLowerCase();
          const stripe = parseInt(session.stripe_count || '0');
          const key = `${belt}_${stripe}`;
          tickCountMap[key] = (tickCountMap[key] || 0) + 1;
        }

        const spacings: number[] = [];

        for (let i = 0; i < DEFAULT_BELTS.length; i++) {
          const belt = DEFAULT_BELTS[i].dbColor.toLowerCase();

          for (let stripe = 0; stripe < 5; stripe++) {
            const key = `${belt}_${stripe}`;
            const tickCount = tickCountMap[key] || 0;

            if (tickCount > 0) {
              const dynamicSpace = tickCount * (TICK_WIDTH + spacingPerTick) + MINIMUM_END_BUFFER;
              spacings.push(Math.max(dynamicSpace, BASE_STRIPE_SPACING));
            } else if (stripe === 0 || stripe === 4) {
              spacings.push(BASE_STRIPE_SPACING + 40); // BELT_ICON_HALF_WIDTH
            } else {
              spacings.push(BASE_STRIPE_SPACING);
            }
          }
        }

        setStripeSpacings(spacings);
      } catch (error) {
        console.error('Error calculating dynamic spacing:', error);
        setStripeSpacings(Array(DEFAULT_BELTS.length * 5).fill(BASE_STRIPE_SPACING));
      }
    }

    fetchTrainingSessions();
  }, [user?.id, isMobile, TICK_WIDTH, spacingPerTick, state.isOnline]);

  return stripeSpacings;
}
